import { Badge } from "@exhousing/design-system";

type Tone = "neutral" | "brand" | "success" | "warning" | "danger" | "info";

// Friendly labels + tones for the raw status codes the API returns.
const MAP: Record<string, { label: string; tone: Tone }> = {
  // contracts (lifecycle: draft → active → ending → closed / cancelled)
  ending: { label: "Ending", tone: "warning" },
  cancelled: { label: "Cancelled", tone: "neutral" },
  signed: { label: "Signed", tone: "success" },
  ended: { label: "Ended", tone: "neutral" },
  archived: { label: "Archived", tone: "neutral" },
  // properties / listings
  draft: { label: "Draft", tone: "neutral" },
  published: { label: "Published", tone: "success" },
  available: { label: "Available", tone: "success" },
  rented: { label: "Rented", tone: "neutral" },
  option: { label: "Under option", tone: "warning" },
  // inspections
  in_progress: { label: "In progress", tone: "warning" },
  "in progress": { label: "In progress", tone: "warning" },
  completed: { label: "Completed", tone: "success" },
  // deposits / generic
  open: { label: "Open", tone: "warning" },
  closed: { label: "Closed", tone: "neutral" },
  pending: { label: "Pending", tone: "warning" },
  running: { label: "Running", tone: "info" },
  failed: { label: "Failed", tone: "danger" },
  // applications (candidate funnel) / tasks
  new: { label: "New", tone: "warning" },
  handled: { label: "Handled", tone: "success" },
  incomplete: { label: "Incomplete", tone: "warning" },
  preselection: { label: "Pre-selection", tone: "info" },
  viewing_planned: { label: "Viewing planned", tone: "info" },
  viewing_done: { label: "Viewing done", tone: "info" },
  documents_requested: { label: "Docs requested", tone: "warning" },
  documents_received: { label: "Docs received", tone: "info" },
  screening: { label: "Screening", tone: "info" },
  approved: { label: "Approved", tone: "success" },
  reserve: { label: "Reserve", tone: "neutral" },
  under_option: { label: "Under option", tone: "warning" },
  contract_phase: { label: "Contract phase", tone: "info" },
  tenant: { label: "Tenant", tone: "success" },
  done: { label: "Done", tone: "success" },
  dismissed: { label: "Dismissed", tone: "neutral" },
  // orgs / users
  active: { label: "Active", tone: "success" },
  inactive: { label: "Inactive", tone: "neutral" },
  suspended: { label: "Suspended", tone: "danger" },
  invited: { label: "Invited", tone: "warning" },
  // billing / invoices / payments
  issued: { label: "Issued", tone: "info" },
  paid: { label: "Paid", tone: "success" },
  partially_paid: { label: "Partially paid", tone: "warning" },
  overdue: { label: "Overdue", tone: "danger" },
  credited: { label: "Credited", tone: "neutral" },
  void: { label: "Void", tone: "neutral" },
  confirmed: { label: "Confirmed", tone: "success" },
  charged_back: { label: "Charged back", tone: "danger" },
  // document requirements (request → submitted → accepted / rejected)
  requested: { label: "Requested", tone: "neutral" },
  submitted: { label: "Submitted", tone: "info" },
  accepted: { label: "Accepted", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  // inbox — message match state
  matched: { label: "Matched", tone: "success" },
  unmatched: { label: "Unmatched", tone: "warning" },
  multiple: { label: "Needs review", tone: "info" },
  // inbox — mailbox connection health
  auth_failed: { label: "Sign-in failed", tone: "danger" },
  unreachable: { label: "Unreachable", tone: "danger" },
  paused: { label: "Paused", tone: "neutral" },
};

function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/** Renders any raw status code as a friendly, consistently-toned badge. */
export function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const m = MAP[status.toLowerCase()] ?? { label: titleCase(status), tone: "neutral" };
  return (
    <Badge tone={m.tone} dot>
      {m.label}
    </Badge>
  );
}
